import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Tooltip,
  Avatar,
  TablePagination
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  VolunteerActivism as VolunteerActivismIcon,
  School as SchoolIcon,
  ReceiptLong as ReceiptLongIcon
} from '@mui/icons-material';
import { esES } from '@mui/x-data-grid/locales';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { transactionService } from '../../services/transactionService';
import { formatCurrency, formatNumber } from '../../utils/formatNumber';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
dayjs.locale('es');

const TransactionHistory: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    total_income: 0,
    total_expenses: 0,
    total_count: 0,
    income_count: 0,
    expense_count: 0,
    net_balance: 0
  });
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20
  });
  
  const [filters, setFilters] = useState({
    transaction_type: '',
    start_date: '',
    end_date: '',
    search: ''
  });

  useEffect(() => {
    loadTransactions();
    loadStats();
  }, [paginationModel, filters]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      
      const params: any = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        ...filters
      };
      
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await transactionService.getAll(params);
      
      setTransactions(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar transacciones');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await transactionService.getStats(filters);
      setStats(response.data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleExport = async () => {
    try {
      const params: any = { ...filters };
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });
      
      const response = await transactionService.exportToExcel(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transacciones_${dayjs().format('YYYY-MM-DD')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Error al exportar transacciones');
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'monthly_payment':
        return <PaymentIcon sx={{ color: '#4caf50' }} />;
      case 'expense':
        return <TrendingDownIcon sx={{ color: '#f44336' }} />;
      case 'donation':
        return <VolunteerActivismIcon sx={{ color: '#9c27b0' }} />;
      case 'income':
        return <TrendingUpIcon sx={{ color: '#2196f3' }} />;
      case 'transfer_in':
        return <AccountBalanceIcon sx={{ color: '#00bcd4' }} />;
      case 'transfer_out':
        return <AccountBalanceIcon sx={{ color: '#ff9800' }} />;
      case 'adjustment':
        return <ReceiptLongIcon sx={{ color: '#607d8b' }} />;
      default:
        return <ReceiptIcon />;
    }
  };

  const getTransactionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      monthly_payment: 'Pago Mensual',
      expense: 'Gasto',
      donation: 'Donación/Ofrenda',
      income: 'Ingreso',
      transfer_in: 'Transferencia (Entrada)',
      transfer_out: 'Transferencia (Salida)',
      adjustment: 'Ajuste'
    };
    return labels[type] || type;
  };

  const formatAmount = (amount: number, type: string) => {
    const isPositive = ['income', 'monthly_payment', 'donation', 'transfer_in'].includes(type);
    const color = isPositive ? 'success.main' : 'error.main';
    const sign = isPositive ? '+' : '-';
    return (
      <Typography variant="body2" fontWeight="bold" color={color}>
        {sign} {formatCurrency(Math.abs(amount))}
      </Typography>
    );
  };

  const columns: GridColDef[] = [
    { 
      field: 'transaction_date', 
      headerName: 'Fecha', 
      width: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            {dayjs(params.value).format('DD/MM/YYYY')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dayjs(params.value).format('HH:mm:ss')}
          </Typography>
        </Box>
      )
    },
    {
      field: 'transaction_type',
      headerName: 'Tipo',
      width: 150,
      renderCell: (params) => (
        <Chip
          icon={getTransactionIcon(params.value)}
          label={getTransactionTypeLabel(params.value)}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'description',
      headerName: 'Descripción',
      width: 300,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography variant="body2" noWrap>
            {params.value}
          </Typography>
        </Tooltip>
      )
    },
    {
      field: 'previous_balance',
      headerName: 'Saldo Anterior',
      width: 130,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    {
      field: 'amount',
      headerName: 'Monto',
      width: 130,
      align: 'right',
      renderCell: (params) => formatAmount(params.value, params.row.transaction_type)
    },    
    {
      field: 'new_balance',
      headerName: 'Nuevo Saldo',
      width: 130,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" color="primary">
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    {
      field: 'reference',
      headerName: 'Referencia',
      width: 150,
      renderCell: (params) => (
        <Typography variant="caption" fontFamily="monospace" color="text.secondary">
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'user',
      headerName: 'Usuario',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.user?.name || 'Sistema'}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Ver detalle">
          <IconButton
            size="small"
            onClick={() => {
              if (params.row.source_type === 'App\\Models\\MonthlyPayment') {
                navigate(`/monthly-payments/${params.row.source_id}`);
              } else if (params.row.source_type === 'App\\Models\\Expense') {
                navigate(`/expenses/${params.row.source_id}`);
              } else if (params.row.source_type === 'App\\Models\\Donation') {
                navigate(`/donations/${params.row.source_id}`);
              } else if (params.row.source_type === 'App\\Models\\TeacherPayment') {
                navigate(`/teacher-payments/${params.row.source_id}`);
              }
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  const transactionTypes = [
    { value: 'monthly_payment', label: 'Pagos Mensuales' },
    { value: 'expense', label: 'Gastos' },
    { value: 'donation', label: 'Donaciones/Ofrendas' },
    { value: 'income', label: 'Ingresos' },
    { value: 'transfer_in', label: 'Transferencias Entrada' },
    { value: 'transfer_out', label: 'Transferencias Salida' },
    { value: 'adjustment', label: 'Ajustes' }
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Historial de Transacciones
        </Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
        >
          Exportar
        </Button>
      </Box>

      {/* Tarjetas de resumen */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Typography variant="body2">Total Ingresos</Typography>
              <Typography variant="h4">
                {formatCurrency(stats.total_income)}
              </Typography>
              <Typography variant="caption">{stats.income_count} transacciones</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
            <CardContent>
              <Typography variant="body2">Total Gastos</Typography>
              <Typography variant="h4">
                {formatCurrency(stats.total_expenses)}
              </Typography>
              <Typography variant="caption">{stats.expense_count} transacciones</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: stats.net_balance >= 0 ? 'info.light' : 'warning.light' }}>
            <CardContent>
              <Typography variant="body2">Balance Neto</Typography>
              <Typography variant="h4">
                {formatCurrency(stats.net_balance)}
              </Typography>
              <Typography variant="caption">Ingresos - Gastos</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Total Transacciones</Typography>
              <Typography variant="h4">{formatNumber(stats.total_count)}</Typography>
              <Typography variant="caption">registros</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por descripción o referencia..."
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select
                value={filters.transaction_type}
                label="Tipo"
                onChange={(e) => setFilters({...filters, transaction_type: e.target.value})}
              >
                <MenuItem value="">Todos</MenuItem>
                {transactionTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              type="date"
              label="Fecha Inicio"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.start_date}
              onChange={(e) => setFilters({...filters, start_date: e.target.value})}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              type="date"
              label="Fecha Fin"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.end_date}
              onChange={(e) => setFilters({...filters, end_date: e.target.value})}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={() => {
                  setPaginationModel({ ...paginationModel, page: 0 });
                  loadTransactions();
                  loadStats();
                }}
                fullWidth
                size="small"
              >
                Filtrar
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setFilters({ transaction_type: '', start_date: '', end_date: '', search: '' });
                  setPaginationModel({ page: 0, pageSize: 20 });
                }}
                size="small"
              >
                <RefreshIcon />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabla */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={transactions}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={total}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          sx={{
            '& .MuiDataGrid-cell': {
              fontSize: '0.875rem',
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default TransactionHistory;